import {
	useQuery,
	UseQueryOptions,
	useQueryClient,
} from '@tanstack/react-query';
import {
	TitanBadgeHasResponse,
	TitanBadgeCountResponse,
	TitanBadgeError,
} from 'src/app/api/titan-badge/types';
import { useSearchParams } from 'next/navigation';
import { useWallet } from '@solana/wallet-adapter-react';

/**
 * Hook to check if a wallet has a Titan badge
 * Automatically detects wallet address from connected wallet or ?authority URL parameter
 */
export const useWalletHasBadge = (
	options?: Omit<
		UseQueryOptions<TitanBadgeHasResponse, TitanBadgeError>,
		'queryKey' | 'queryFn'
	>
) => {
	const { publicKey } = useWallet();
	const searchParams = useSearchParams();

	// Check for authority parameter in URL first, then fall back to connected wallet
	const authorityParam = searchParams?.get('authority');
	const walletAddress = authorityParam || publicKey?.toBase58();

	return useQuery<TitanBadgeHasResponse, TitanBadgeError>({
		queryKey: ['titanBadge', 'hasBadge', walletAddress],
		queryFn: async (): Promise<TitanBadgeHasResponse> => {
			if (!walletAddress) {
				throw new Error('No wallet address available');
			}

			const response = await fetch(
				`/api/titan-badge?action=has-badge&wallet_address=${encodeURIComponent(
					walletAddress
				)}`
			);

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw {
					error: errorData.error || 'Failed to check badge status',
					status: response.status,
					statusText: response.statusText,
				} as TitanBadgeError;
			}

			return response.json();
		},
		enabled: !!walletAddress,
		staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
		gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
		retry: (failureCount, error) => {
			// Don't retry on client errors (4xx), but retry on server errors (5xx)
			if (
				'status' in error &&
				error.status &&
				error.status >= 400 &&
				error.status < 500
			) {
				return false;
			}
			return failureCount < 3;
		},
		refetchInterval: (query) => {
			// Refetch every 10 seconds if user doesn't have badge yet
			// Use a simple time-based limit: stop after 100 seconds (10 attempts * 10 seconds)
			const data = query.state.data;
			const dataUpdatedAt = query.state.dataUpdatedAt;
			const now = Date.now();

			// If no data has been fetched yet, or if we have data showing no badge
			if (data && !data.has_badge) {
				// Stop refetching after 100 seconds (10 attempts * 10 seconds)
				if (dataUpdatedAt && now - dataUpdatedAt > 100000) {
					return false;
				}
				return 10 * 1000; // 10 seconds
			}
			return false; // Don't refetch
		},
		...options,
	});
};

/**
 * Hook to get the total Titan badge count
 */
const useTitanBadgeCount = (
	options?: Omit<
		UseQueryOptions<TitanBadgeCountResponse, TitanBadgeError>,
		'queryKey' | 'queryFn'
	>
) => {
	return useQuery<TitanBadgeCountResponse, TitanBadgeError>({
		queryKey: ['titanBadge', 'count'],
		queryFn: async (): Promise<TitanBadgeCountResponse> => {
			const response = await fetch('/api/titan-badge?action=badge-count');

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw {
					error: errorData.error || 'Failed to fetch badge count',
					status: response.status,
					statusText: response.statusText,
				} as TitanBadgeError;
			}

			return response.json();
		},
		staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
		gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
		retry: (failureCount, error) => {
			// Don't retry on client errors (4xx), but retry on server errors (5xx)
			if (
				'status' in error &&
				error.status &&
				error.status >= 400 &&
				error.status < 500
			) {
				return false;
			}
			return failureCount < 3;
		},
		refetchInterval: 30 * 1000, // Refetch every 30 seconds to keep count updated
		...options,
	});
};

/**
 * Utility hook that combines both badge queries for a wallet
 * Useful when you need both the wallet's badge status and the total count
 * Automatically detects wallet address from connected wallet or ?authority URL parameter
 */
export const useTitanBadgeData = (options?: {
	hasBadgeOptions?: Omit<
		UseQueryOptions<TitanBadgeHasResponse, TitanBadgeError>,
		'queryKey' | 'queryFn'
	>;
	countOptions?: Omit<
		UseQueryOptions<TitanBadgeCountResponse, TitanBadgeError>,
		'queryKey' | 'queryFn'
	>;
}) => {
	const hasBadgeQuery = useWalletHasBadge(options?.hasBadgeOptions);
	const countQuery = useTitanBadgeCount(options?.countOptions);

	return {
		hasBadge: hasBadgeQuery,
		count: countQuery,
		isLoading: hasBadgeQuery.isLoading || countQuery.isLoading,
		isError: hasBadgeQuery.isError || countQuery.isError,
		error: hasBadgeQuery.error || countQuery.error,
	};
};

/**
 * Hook to invalidate Titan badge queries after a successful swap
 * Call this after a successful Titan swap to check for newly earned badges
 */
export const useInvalidateTitanBadgeQueries = () => {
	const queryClient = useQueryClient();
	const { publicKey } = useWallet();
	const searchParams = useSearchParams();

	// Get the wallet address (same logic as useWalletHasBadge)
	const authorityParam = searchParams?.get('authority');
	const walletAddress = authorityParam || publicKey?.toBase58();

	return {
		/**
		 * Invalidates both badge status and count queries
		 * Use this after a successful Titan swap
		 */
		invalidateAll: () => {
			// Invalidate the specific wallet's badge status
			if (walletAddress) {
				queryClient.invalidateQueries({
					queryKey: ['titanBadge', 'hasBadge', walletAddress],
				});
			}

			// Invalidate the badge count (may have increased)
			queryClient.invalidateQueries({
				queryKey: ['titanBadge', 'count'],
			});
		},

		/**
		 * Invalidates only the wallet's badge status
		 * Use when you only need to check if the current wallet earned a badge
		 */
		invalidateBadgeStatus: () => {
			if (walletAddress) {
				queryClient.invalidateQueries({
					queryKey: ['titanBadge', 'hasBadge', walletAddress],
				});
			}
		},

		/**
		 * Invalidates only the badge count
		 * Use when you only need to update the total count
		 */
		invalidateBadgeCount: () => {
			queryClient.invalidateQueries({
				queryKey: ['titanBadge', 'count'],
			});
		},
	};
};
